import React, { useState } from "react";
import CreatableSelect from "react-select/creatable";
import { Label } from "../components/ui/label";

const MultiEmailSelect = ({
  label,
  selectedEmails = [],
  onChange,
  isDisabled = false,
  isMulti = true,
  showRemoveIcon = true,
}) => {
  const [inputValue, setInputValue] = useState("");
  const [error, setError] = useState("");
  const [pendingValue, setPendingValue] = useState("");

  const domains = ["@gmail.com", "@hotmail.com", "@yahoo.com"];

  const formattedEmails = isMulti
    ? selectedEmails.map((email) => ({ value: email, label: email }))
    : selectedEmails[0]
    ? { value: selectedEmails[0], label: selectedEmails[0] }
    : null;

  const isValidEmail = (email) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);

  const getDomainSuggestions = (input) => {
    if (!input || input.includes("@")) return [];
    return domains.map((domain) => ({
      value: input + domain,
      label: `${input}${domain}`,
    }));
  };

  const handleChange = (selectedOption, actionMeta) => {
    if (actionMeta.action === "create-option") {
      // Clear the pending value when an option is explicitly created
      setPendingValue("");
    }

    const emailValues = isMulti
      ? selectedOption?.map((item) => item.value) || []
      : selectedOption
      ? [selectedOption.value]
      : [];
    onChange(emailValues);
  };

  const handleCreate = (inputValue) => {
    if (!isValidEmail(inputValue)) {
      setError("Invalid email address");
      return;
    }
    // Clear error when email is valid
    setError("");
    const newEmail = { value: inputValue, label: inputValue };
    handleChange(isMulti ? [...(formattedEmails || []), newEmail] : newEmail, {
      action: "create-option",
    });
    setInputValue("");
  };

  const handleBlur = () => {
    if (inputValue && isValidEmail(inputValue)) {
      handleCreate(inputValue);
    } else if (inputValue) {
      setError("Invalid email address");
    } else {
      // Clear error when input is empty
      setError("");
    }
    setPendingValue("");
  };

  const handleInputChange = (inputValue, actionMeta) => {
    if (
      actionMeta.action !== "input-blur" &&
      actionMeta.action !== "menu-close"
    ) {
      setInputValue(inputValue);
      setPendingValue(inputValue);
      // Clear error when user starts typing again
      if (error) {
        setError("");
      }
    }
  };

  const customStyles = {
    control: (base, { isFocused }) => ({
      ...base,
      backgroundColor: "#ffffff",
      border: "1px solid #d1d5db",
      borderBottom: isFocused ? "2px solid #14B8A6" : "1px solid #d1d5db",
      borderRadius: "0.375rem",
      minHeight: "40px",
      boxShadow: "none",
      "&:hover": {
        borderColor: "#d1d5db",
        borderBottomColor: isFocused ? "#14B8A6" : "#9ca3af",
      },
    }),
    valueContainer: (base) => ({
      ...base,
      padding: "2px 8px",
    }),
    multiValue: (base) => ({
      ...base,
      backgroundColor: "#e5e7eb",
    }),
    multiValueLabel: (base) => ({
      ...base,
      color: "#111827",
      padding: "2px 6px",
    }),
    multiValueRemove: (base) => ({
      ...base,
      display: showRemoveIcon ? "flex" : "none",
      ":hover": {
        backgroundColor: "#d1d5db",
        color: "#111827",
      },
    }),
    input: (base) => ({
      ...base,
      color: "#111827",
      margin: 0,
      padding: 0,
    }),
    placeholder: (base) => ({
      ...base,
      color: "#6b7280",
      textAlign: "left",
    }),
    menu: (base) => ({
      ...base,
      backgroundColor: "#ffffff",
      border: "1px solid #e5e7eb",
      borderRadius: "0.375rem",
      boxShadow: "0 1px 2px 0 rgba(0, 0, 0, 0.05)",
      marginTop: "0.25rem",
    }),
    menuList: (base) => ({
      ...base,
      padding: "0.25rem",
    }),
    option: (base, { isFocused, isSelected }) => ({
      ...base,
      backgroundColor: isFocused ? "#f0fdf4" : "#ffffff",
      color: isFocused ? "#14B8A6" : "#111827",
      borderRadius: "0.125rem",
      fontSize: "0.875rem",
      lineHeight: "1.25rem",
      padding: "0.5rem 0.75rem",
      "&:active": {
        backgroundColor: "#dcfce7",
      },
    }),
    singleValue: (base) => ({
      ...base,
      color: "#111827",
    }),
    dropdownIndicator: (base) => ({
      ...base,
      color: "#6b7280",
      padding: "0 8px",
      ":hover": {
        color: "#4b5563",
      },
    }),
    indicatorSeparator: () => ({
      display: "none",
    }),
  };

  return (
    <div className="space-y-1">
      {label && <Label>{label}</Label>}
      <CreatableSelect
        className="w-full text-sm"
        value={formattedEmails}
        onChange={handleChange}
        onCreateOption={handleCreate}
        isMulti={isMulti}
        isClearable={false}
        isSearchable
        placeholder="Enter email address..."
        isDisabled={isDisabled}
        inputValue={inputValue}
        onInputChange={handleInputChange}
        onBlur={handleBlur}
        options={getDomainSuggestions(inputValue)}
        styles={customStyles}
        filterOption={null}
        noOptionsMessage={() =>
          inputValue.includes("@")
            ? "Press Enter to add email"
            : "Type username to see domain suggestions"
        }
        closeMenuOnSelect={!isMulti}
        blurInputOnSelect={false}
        components={{
          DropdownIndicator: () => (
            <svg
              width="15"
              height="15"
              viewBox="0 0 15 15"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
              className="mr-2 opacity-50"
            >
              <path
                d="M4.93179 5.43179C4.75605 5.60753 4.75605 5.89245 4.93179 6.06819C5.10753 6.24392 5.39245 6.24392 5.56819 6.06819L7.49999 4.13638L9.43179 6.06819C9.60753 6.24392 9.89245 6.24392 10.0682 6.06819C10.2439 5.89245 10.2439 5.60753 10.0682 5.43179L7.81819 3.18179C7.73379 3.0974 7.61933 3.04999 7.49999 3.04999C7.38064 3.04999 7.26618 3.0974 7.18179 3.18179L4.93179 5.43179ZM10.0682 9.56819C10.2439 9.39245 10.2439 9.10753 10.0682 8.93179C9.89245 8.75606 9.60753 8.75606 9.43179 8.93179L7.49999 10.8636L5.56819 8.93179C5.39245 8.75606 5.10753 8.75606 4.93179 8.93179C4.75605 9.10753 4.75605 9.39245 4.93179 9.56819L7.18179 11.8182C7.35753 11.9939 7.64245 11.9939 7.81819 11.8182L10.0682 9.56819Z"
                fill="currentColor"
                fillRule="evenodd"
                clipRule="evenodd"
              ></path>
            </svg>
          ),
        }}
      />
      {error && <p className="text-red-500 text-sm mt-1">{error}</p>}
    </div>
  );
};

export default MultiEmailSelect;
